import { Fragment } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { graphql } from 'gatsby'
import {
  Section,
  Container,
  ListItem,
  List,
  Button,
} from 'chaoskit/src/components'
import { linkUnderline } from 'chaoskit/src/assets/styles/utility/link'
import { generateGradient } from 'chaoskit/src/assets/styles/utility/gradient'
import { link, text } from 'chaoskit/src/assets/styles/utility'
import { MDXRenderer } from 'gatsby-plugin-mdx'

import Link from '~components/Link'
import Data from '~components/Data'
import BaseLayout from '~layouts/BaseLayout'
import Author from '~partials/global/Author'
import ArticleCTA from '~partials/global/ArticleCTA'
import HelmetSEO from '~partials/global/HelmetSEO'
import NodeTypeTracker from '~partials/global/NodeTypeTracker'
import SocialWidget from '~partials/blog/SocialWidget'
import { stickyIt } from '~styles/utility'
import TextLink from '~components/TextLink'
import PostHero from '~partials/blog/PostHero'
import useSiteMetadata from '~hooks/useSiteMetadata'
import CustomCanonical from '~components/CustomCanonical'

const ArticleWrapper = ({ as: Component = 'article', ...rest }) => {
  const theme = useTheme()

  return (
    <Component
      css={{
        display: 'grid',
        gap: theme.space.large,
        gridTemplateAreas: `"author author"
                            "main main"
                            "cta cta"
                            "related related"`,
        gridTemplateColumns: 'minmax(0, 1fr)',

        [theme.mq.medium]: {
          gridTemplateAreas: `"author main"
                              "related main"
                              "cta main"`,
          gridTemplateColumns: '300px minmax(0, 1fr)',
          rowGap: theme.space.large * 3,
        },
      }}
      {...rest}
    />
  )
}

ArticleWrapper.propTypes = {
  as: PropTypes.any,
}

const AuthorWidget = ({ author, authorUrl }) => {
  const theme = useTheme()

  return (
    <aside css={{ gridArea: 'author' }}>
      <div css={{ [theme.mq.medium]: [stickyIt(theme)] }}>
        <Author name={author} size="large" url={authorUrl} />
      </div>
    </aside>
  )
}

AuthorWidget.propTypes = {
  author: PropTypes.string.isRequired,
  authorUrl: PropTypes.string.isRequired,
}

const RelatedPostsWidget = ({ data, ...rest }) => {
  const theme = useTheme()

  if (data?.edges?.length < 1) return null

  return (
    <aside css={{ gridArea: 'related' }} {...rest}>
      <div css={{ [theme.mq.medium]: [stickyIt(theme)] }}>
        <h6
          css={{
            padding: theme.space.base,
            marginBottom: '0',
          }}
        >
          Related
        </h6>
        <List css={{ marginTop: 0 }}>
          {data.edges.map(({ node }) => (
            <ListItem
              key={node.fileAbsolutePath}
              css={{
                padding: theme.space.base,
                borderImageSlice: 1,
                borderImageSource: generateGradient({
                  start: theme.brand.violet,
                  stop: theme.brand.orchid400,
                }),
                borderStyle: 'solid',
                borderWidth: 0,
                borderTopWidth: 2,
              }}
            >
              <TextLink
                href={node.fields.fullUrl}
                css={[
                  link.reset(theme),
                  linkUnderline,
                  {
                    fontWeight: theme.fontWeight.bold,
                    ...theme.text.large__fluid,
                    lineHeight: theme.lineHeight.small,
                  },
                ]}
              >
                {node.frontmatter.title}
              </TextLink>
              <footer
                css={{
                  marginTop: theme.space.xsmall,
                  ...theme.text.small,
                  color: theme.fontColor.muted,
                }}
              >
                {node.frontmatter.date} - {node.timeToRead} min read
              </footer>
            </ListItem>
          ))}
        </List>
      </div>
    </aside>
  )
}

RelatedPostsWidget.propTypes = {
  data: PropTypes.object.isRequired,
}

const ArticleCTAWidget = ({ articleCTA, ...rest }) => {
  const theme = useTheme()

  return (
    <div css={{ gridArea: 'cta' }} {...rest}>
      <div css={{ [theme.mq.medium]: [stickyIt(theme)] }}>
        <ArticleCTA cta={articleCTA} collection="blog" type="aside" />
      </div>
    </div>
  )
}

ArticleCTAWidget.propTypes = {
  articleCTA: PropTypes.string,
}

const ArticleBody = (props) => <div css={{ gridArea: 'main' }} {...props} />

const LatestPosts = ({ data, siteUrl }) => {
  const theme = useTheme()

  if (data?.edges?.length < 1) return null

  return (
    <Section>
      <div
        css={{
          display: 'grid',
          gap: theme.space.large,

          [theme.mq.medium]: {
            gap: theme.space.xlarge,
          },
        }}
      >
        {data.edges.map(({ node }) => {
          // Workaround for Gatsby's location prop not reliably being available on server-loads
          const currentPageUrl = `${siteUrl}${node.fields.fullUrl}`

          return (
            <article key={node.frontmatter.title}>
              <PostHero
                currentPageUrl={currentPageUrl}
                date={node.frontmatter.date}
                excerpt={node.excerpt}
                featuredImage={node.fields.featuredImage}
                timeToRead={node.timeToRead}
                title={node.frontmatter.title}
                fullUrl={node.fields.fullUrl}
                css={{ marginBottom: theme.space.large }}
                hasLink
              />
              <Container>
                <ArticleWrapper
                  as="div"
                  css={{
                    gridTemplateAreas: `"author author"
                                    "main main"`,

                    [theme.mq.medium]: {
                      gridTemplateAreas: `"author main"`,
                    },
                  }}
                >
                  <AuthorWidget
                    author={node.frontmatter.author}
                    authorUrl={node.fields.authorUrl}
                  />
                  <ArticleBody>
                    <Data>{node.excerpt}</Data>

                    <div css={{ marginTop: theme.space.large }}>
                      <Button type="default" url={node.fields.fullUrl}>
                        Read more
                      </Button>
                    </div>
                  </ArticleBody>
                </ArticleWrapper>
              </Container>
            </article>
          )
        })}
      </div>
    </Section>
  )
}

LatestPosts.propTypes = {
  data: PropTypes.object.isRequired,
  siteUrl: PropTypes.string.isRequired,
}

const Categories = ({ data }) => {
  const theme = useTheme()

  if (data.length === 0) return null

  return (
    <div>
      <h6
        css={{
          borderImageSlice: 1,
          borderImageSource: generateGradient({
            start: theme.brand.violet,
            stop: theme.brand.orchid400,
          }),
          borderStyle: 'solid',
          borderWidth: 0,
          borderBottomWidth: 2,
          paddingBottom: theme.space.base,
          marginBottom: theme.space.base,
        }}
      >
        Categories
      </h6>
      {data.map((category, idx) => (
        <Fragment key={category.name}>
          {!!idx && ', '}
          <Link
            css={[
              linkUnderline,
              link.reset(theme),
              {
                fontWeight: theme.fontWeight.bold,
              },
            ]}
            to={category.slug}
          >
            {category.name}
          </Link>
        </Fragment>
      ))}
    </div>
  )
}

Categories.propTypes = {
  data: PropTypes.array,
}

const Post = ({
  data: {
    post: {
      body,
      excerpt,
      timeToRead,
      frontmatter: {
        title,
        description,
        canonical,
        date,
        dateTime,
        author,
        categories,
        socialMediaImage,
        articleCTA,
      },
      fields: { authorUrl, categoryUrls, featuredImage, fullUrl },
    },
    related,
    latest,
  },
  pageContext: { nodeType },
}) => {
  const theme = useTheme()

  const { siteUrl } = useSiteMetadata()

  // Workaround for Gatsby's location prop not reliably being available on server-loads
  const currentPageUrl = `${siteUrl}${fullUrl}`

  // Get list of categories with URLs
  const categoryData = []

  if (categories) {
    categories.forEach((category, index) => {
      categoryData.push({
        name: category,
        slug: categoryUrls[index],
      })
    })
  }

  return (
    <BaseLayout
      headerProps={{ hasDefaultOverlay: true }}
      footerProps={{ hideCTA: true }}
    >
      <HelmetSEO
        title={title}
        description={description || excerpt}
        image={socialMediaImage}
        type="article"
      />
      {/** Overrides auto-generated canonical tag */}
      {canonical && <CustomCanonical canonical={canonical} />}
      <NodeTypeTracker nodeType={nodeType} />
      <PostHero
        currentPageUrl={currentPageUrl}
        date={date}
        dateTime={dateTime}
        excerpt={excerpt}
        featuredImage={featuredImage}
        timeToRead={timeToRead}
        title={title}
        fullUrl={fullUrl}
        isHero
      />
      <Section>
        <Container>
          <ArticleWrapper>
            <AuthorWidget author={author} authorUrl={authorUrl} />
            <RelatedPostsWidget data={related} />
            <ArticleCTAWidget articleCTA={articleCTA} />

            <ArticleBody>
              <div
                css={{
                  a: [
                    text.underline,
                    link.base(theme),
                    { fontWeight: theme.fontWeight.bold },
                  ],
                }}
              >
                <MDXRenderer>{body}</MDXRenderer>
              </div>

              <footer
                css={{
                  display: 'grid',
                  gap: theme.space.large,
                  marginTop: theme.space.large,
                }}
              >
                <SocialWidget currentPageUrl={currentPageUrl} title={title} />
                <Categories data={categoryData} />
              </footer>
            </ArticleBody>
          </ArticleWrapper>
        </Container>
      </Section>
      <LatestPosts data={latest} siteUrl={siteUrl} />
    </BaseLayout>
  )
}

Post.propTypes = {
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.object.isRequired,
}

export default Post

export const pageQuery = graphql`
  query PostSingle(
    $slug: String!
    $categories: [String]
    $fileAbsolutePath: String!
  ) {
    post: mdx(
      fields: { slug: { eq: $slug } }
      fileAbsolutePath: { regex: "/src/posts/" }
    ) {
      body
      excerpt
      timeToRead
      frontmatter {
        title
        description
        canonical
        date: date(formatString: "LL")
        dateTime: date(formatString: "YYYY/MM/DD")
        slug
        categories
        author
        socialMediaImage
        articleCTA {
          ...ArticleCTAFragment
        }
      }
      fields {
        slug
        fullUrl
        categoryUrls
        authorUrl
        featuredImage
      }
    }

    related: allMdx(
      limit: 3
      filter: {
        frontmatter: { categories: { in: $categories } }
        # Make sure we're only querying posts
        # Filter out current post
        fileAbsolutePath: { regex: "/src/posts/", ne: $fileAbsolutePath }
      }
      sort: { fields: [frontmatter___date], order: DESC }
    ) {
      edges {
        node {
          ...PostTeaserFragment
        }
      }
    }

    latest: allMdx(
      limit: 2
      filter: {
        # Make sure we're only querying posts
        # Filter out current post
        fileAbsolutePath: { regex: "/src/posts/", ne: $fileAbsolutePath }
      }
      sort: { fields: frontmatter___date, order: DESC }
    ) {
      edges {
        node {
          ...PostTeaserFragment
        }
      }
    }
  }
`
